import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface FinancialSummary {
  period: {
    startDate: string;
    endDate: string;
    name: string;
  };
  summary: {
    assets: {
      total: number;
      current: number;
      nonCurrent: number;
    };
    liabilities: {
      total: number;
      current: number;
      nonCurrent: number;
    };
    equity: {
      total: number;
    };
    revenue: {
      total: number;
    };
    expenses: {
      total: number;
    };
    netIncome: number;
  };
  accountCount: number;
  isBalanced: boolean;
}

export interface CashFlowSummary {
  period: {
    startDate: string;
    endDate: string;
  };
  netCashFlow: number;
  totalInflows: number;
  totalOutflows: number;
  transactionCount: number;
}

export interface ManualJournalEntry {
  date: string;
  description: string;
  reference?: string;
  lines: Array<{
    accountId: string;
    debit: number;
    credit: number;
    description?: string;
  }>;
  autoPost?: boolean;
}

export interface TrialBalanceValidation {
  isBalanced: boolean;
  totalDebits: number;
  totalCredits: number;
  difference: number;
  asOfDate: string;
}

// API Functions
export const accountingAPI = {
  // Check what was created during initialization
  getInitializationStatus: (siteId: string) => {
    console.log('Checking initialization status...');
    return api.get(`/accounting/sites/${siteId}/status`).catch(error => {
      console.error('Status check error:', error);
      throw error;
    });
  },

  // Initialize accounting system
  initializeAccounting: (siteId: string) => {
    const url = `/accounting/sites/${siteId}/initialize`;
    console.log('API call: initializeAccounting');
    console.log('Full URL:', url);
    console.log('SiteId:', siteId);
    
    return api.post(url).catch(error => {
      console.error('API Error in initializeAccounting:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error config:', error.config);
      throw error;
    });
  },

  // Get financial summary
  getFinancialSummary: (siteId: string) => {
    return api.get<FinancialSummary>(`/accounting/sites/${siteId}/summary`);
  },

  // Get cash flow summary
  getCashFlowSummary: (siteId: string, startDate: string, endDate: string) => {
    return api.get<CashFlowSummary>(`/accounting/sites/${siteId}/cash-flow`, {
      params: { startDate, endDate }
    });
  },

  // Create manual journal entry
  createJournalEntry: (siteId: string, entryData: ManualJournalEntry) => {
    return api.post(`/accounting/sites/${siteId}/journal-entries`, entryData);
  },

  // Reverse journal entry
  reverseJournalEntry: (siteId: string, entryId: string, reason: string) => {
    return api.post(`/accounting/sites/${siteId}/journal-entries/${entryId}/reverse`, {
      reason
    });
  },

  // Validate trial balance
  validateTrialBalance: (siteId: string, asOfDate: string) => {
    return api.get<TrialBalanceValidation>(`/accounting/sites/${siteId}/trial-balance/validate`, {
      params: { asOfDate }
    });
  }
};

export default accountingAPI;
